import React, { Component } from 'react'
import QRCodeReact from 'qrcode.react'
import { Link } from 'react-router-dom'

import BookResource from './resource'
import BookText from './text'
import BookAction from './action'
import Diagnostic from './diagnostic'
import checks from '../../../lib/check'
import checkPath from './lib/checkPath'
import { moduleLabel } from '../../../stores'

// Pastille Oui/Non homogène (remplace les fa-stack + icône barrée)
const BoolFlag = ({ label, value }) => (
  <span className={'bool-flag ' + (value ? 'on' : 'off')}>
    <i className={'fa ' + (value ? 'fa-check' : 'fa-times')} /> {label}
  </span>
)

// Pastille neutre pour signaler un réglage non-défaut (affiché seulement si actif)
const NoteFlag = ({ label }) => (
  <span className="note-flag">
    <i className="fa fa-ban" /> {label}
  </span>
)

// Ligne de paramètre « libellé : valeur »
const ModuleParam = ({ label, value }) =>
  value == null || value === '' ? null : (
    <div className="module-param">
      <span className="module-param-label">{label}</span>
      <span className="module-param-value">{value}</span>
    </div>
  )

// Carte QR uniforme (taille fixe) + code correspondant.
// Accepte une chaîne ou un objet { code, points }.
const QRCard = ({ code, note }) => {
  const isObject = code !== null && typeof code === 'object'
  const value = isObject ? code.code : code
  const autoNote = isObject && code.points != null ? code.points + ' pts' : null
  const finalNote = note != null ? note : autoNote
  return (
    <div className="qr-card">
      <QRCodeReact value={value || ''} size={110} />
      <div className="qr-card-code">{value || ''}</div>
      {finalNote && <div className="qr-card-note">{finalNote}</div>}
    </div>
  )
}

// Carte d'un bouton d'action (icône + action)
const ToolButtonCard = ({ icon, action, path, chapters, highlights }) => (
  <div className="tool-button-card">
    {icon && <BookResource id={icon} path={path} highlights={highlights} style={{ maxWidth: '40px' }} />}
    <BookAction action={action} chapters={chapters} />
  </div>
)

// Résumé des « secondes chances » (retries)
const Retries = ({ retries }) => {
  if (!retries || retries.length === 0) {
    return null
  }
  return (
    <div className="aside-media-group">
      <div className="field-label">Secondes chances</div>
      {retries.map((r, i) => (
        <div className="retry-item" key={i}>
          <ModuleParam label="Seuil de déclenchement" value={r.threshold != null ? r.threshold + ' %' : null} />
          <ModuleParam label="Pénalité" value={r.percentPenalty != null ? r.percentPenalty + ' %' : null} />
          <ModuleParam label="Limite de temps" value={r.timeLimit != null ? r.timeLimit + ' s' : null} />
        </div>
      ))}
    </div>
  )
}

const AugmentedReality = ({ module, highlights }) => {
  const { targets } = module
  return (
    <div>
      {(targets || []).map(({ name, type, autoplay, resource, source }, index) => {
        const { resource: sourceFile, width } = source || {}
        return (
          <div key={name}>
            <h3>
              {name} ({type})
            </h3>
            <div style={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
              <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                <h4>Source</h4>
                <BookResource
                  id={sourceFile}
                  title={`Largeur ${width} mètres`}
                  path={'targets°|°' + index + '°|°source°|°resource'}
                  highlights={highlights}
                  style={{ maxWidth: '200px', maxHeight: '200px', margin: '10px' }}
                />
              </div>
              <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                <h4>Remplacé par</h4>
                <BookResource
                  id={resource}
                  path={'targets°|°' + index + '°|°resource'}
                  highlights={highlights}
                  style={{ maxWidth: '200px', maxHeight: '200px', margin: '10px' }}
                />
                {autoplay && (
                  <span>
                    <i className="fa fa-play" /> Lecture automatique de la vidéo
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const Choice = ({ module, chapters, highlights }) => {
  const { choices, defaultChoice } = module

  return (
    <div>
      {(choices || []).map(({ _id, label, picture, actions }, index) => {
        const actionsClassName = highlights && checkPath(highlights, 'choices°|°' + index + 'actions') ? 'highlighted' : ''
        const isDefault = defaultChoice === _id
        return (
          <div key={index} className="choice-row">
            {picture && (
              <BookResource
                id={picture}
                path={'choices°|°' + index + '°|°picture'}
                highlights={highlights}
                styleResource={{ maxHeight: '60px', width: 'auto' }}
              />
            )}
            <div className="choice-body">
              <div className="choice-header">
                <BookText
                  collection="modules"
                  id={module._id}
                  path={'choices°|°' + index + '°|°label'}
                  text={label}
                  style={{ display: 'inline-block', marginTop: 0 }}
                  highlights={highlights}
                />
                {isDefault && <span className="choice-default-badge">Par défaut</span>}
              </div>
              <div className={actionsClassName}>
                {(actions || []).map((action, i) => (
                  <BookAction action={action} chapters={chapters} key={i} />
                ))}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
const ConversationTextVoice = ({ collection, path, id, sound, characterName, text, highlights }) => {
  return (
    <div className="dialog-text">
      {characterName && <span className="dialog-speaker">{characterName}</span>}
      <BookResource id={sound} path={path + 'sound'} highlights={highlights} />
      <BookText
        collection={collection}
        id={id}
        path={path + 'text'}
        text={text}
        style={{ marginTop: 0 }}
        highlights={highlights}
      />
    </div>
  )
}

const ConversationImages = ({ background, icon, animate, position, path, highlights }) => {
  if (!icon) {
    return null
  }
  const fromLeft = position === 'bottom|start'
  return (
    <div className="dialog-images">
      {background && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <BookResource id={background} highlights={highlights} path={path ? path + 'background' : null} />
          <BookResource
            id={icon}
            style={{
              position: 'absolute',
              right: fromLeft ? 'auto' : 0,
              left: fromLeft ? 0 : 'auto',
              bottom: 0,
              top: 0
            }}
            noBackgroundMesh
            highlights={highlights}
            path={path + 'icon'}
          />
        </div>
      )}
      {!background && <BookResource id={icon} highlights={highlights} path={path + 'icon'} />}

      {animate && (
        <i
          className={
            'fa fa-2x ' +
            (fromLeft ? ' fa-angle-double-right dialog-animate-icon-right' : ' fa-angle-double-left dialog-animate-icon-left')
          }
        />
      )}
    </div>
  )
}

const Conversation = ({ module, chapters, highlights }) => {
  const experience = chapters[module.experienceId].experience || {}
  const characters = experience.characters || []

  return (module.dialogs || []).map(
    ({ icon, sound, text, position, animate, character: characterAndVariantId, background }, index) => {
      const [characterId, variantId] = (characterAndVariantId || '').split('°|°')
      let { label: characterName, variants } = characters.find(({ _id }) => characterId === _id) || {}
      if (variantId && variants) {
        const variant = variants.find(({ _id }) => variantId === _id)
        if (variant && variant.label) {
          characterName = (characterName || '') + ' ' + variant.label
        }
      }
      return (
        <div className={'dialog ' + (position === 'bottom|start' ? 'from-left' : 'from-right')} key={index}>
          {position === 'bottom|start' && (
            <ConversationImages
              background={background || module.background}
              icon={icon}
              animate={animate}
              position={position}
              highlights={highlights}
            />
          )}
          <ConversationTextVoice
            collection="modules"
            id={module._id}
            path={'dialogs°|°' + index + '°|°'}
            text={text}
            sound={sound}
            characterName={characterName}
            highlights={highlights}
          />
          {position !== 'bottom|start' && (
            <ConversationImages
              background={background || module.background}
              icon={icon}
              animate={animate}
              position={position}
              path={'dialogs°|°' + index + '°|°'}
              highlights={highlights}
            />
          )}
        </div>
      )
    }
  )
}
const Compass = () => {
  return <em>Rien à paramétrer sur ce module</em>
}

const Diaporama = ({ module, highlights }) => {
  const { _id, pictures, autoplay, sound, text } = module
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {(pictures || []).map((picture, index) => (
          <BookResource
            id={picture.file}
            key={picture.file}
            path={'pictures°|°' + index + '°|°file'}
            highlights={highlights}
            style={{ width: '200px', height: '200px' }}
            styleResource={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ))}
      </div>
      <div>
        <BoolFlag label="Passage automatique des images" value={autoplay} />
      </div>
      <BookResource id={sound} path="sound" highlights={highlights} />
      <BookText text={text} collection="modules" id={_id} path="text" highlights={highlights} />
    </div>
  )
}
const DragDrop = ({ module, highlights }) => {
  const { picture, drops, drags, dropType } = module
  const indexes = {}
  ;(drops || []).forEach(({ _id }, index) => (indexes[_id] = index + 1))

  return (
    <div>
      <div style={{ marginBottom: '10px' }}>
        Mécanique de jeu :{dropType === 'normal' && <b> Pièces à côté du puzzle, validation finale en cliquant sur le bouton</b>}
        {dropType === 'validated' && <b> Pièces à côté du puzzle, validation immédiate lors de la dépose sur une zone de drop</b>}
        {dropType === 'distributed' && (
          <b> Pièces distribuées au hasard dans les zone de dépose, validation finale en cliquant sur le bouton</b>
        )}
      </div>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <BookResource
          id={picture}
          style={{ display: 'block' }}
          styleResource={{ maxHeight: '500px', width: 'auto' }}
          path="picture"
          highlights={highlights}
        />
        {(drops || []).map(({ _id, left, right, bottom, top }, index) => {
          return (
            <div
              key={_id}
              style={{
                position: 'absolute',
                top: top * 100 + '%',
                left: left * 100 + '%',
                width: (right - left) * 100 + '%',
                height: (bottom - top) * 100 + '%',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px dashed white',
                boxShadow: '1px 1px 12px #555',
                color: 'white',
                textShadow: `-1px 0 0 rgba(0, 0, 0, 0.8), 
             1px 0 0 rgba(0, 0, 0, 0.8), 
             0 -1px 0 rgba(0, 0, 0, 0.8), 
             0 1px 0 rgba(0, 0, 0, 0.8)`,

                textAlign: 'center',
                zIndex: 1,
                overflow: 'hidden',
                borderRadius: '6px',
                pointerEvents: 'none'
              }}
            >
              {index + 1}
            </div>
          )
        })}
      </div>
      <h3>Pièces à placer</h3>
      <div>
        {(drags || []).map(({ points, picture, caption, _id, answers, drop }, index) => {
          const indexAnswers = (answers || []).map(_id => indexes[_id])
          return (
            <BookResource
              id={picture}
              title={`${caption || ''} - Zone ${indexAnswers.join(', ')} - ${points} pts ${
                drop && dropType === 'distributed' ? '- zone de départ : ' + indexes[drop] : ''
              }`}
              key={_id}
              path={'drags°|°' + index + '°|°picture'}
              highlights={highlights}
              style={{ maxWidth: '120px', maxHeight: '120px', margin: '4px' }}
            />
          )
        })}
      </div>
    </div>
  )
}

const Differences = ({ module, highlights }) => {
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <BookResource
        id={module.picture}
        style={{ maxWidth: '100%' }}
        styleResource={{ maxHeight: '600px', width: 'auto' }}
        hd
        path="picture"
        highlights={highlights}
      />
      {(module.differences || []).map(({ _id, top, left }) => {
        return (
          <i
            key={_id}
            className="fa fa-crosshairs"
            style={{
              position: 'absolute',
              top: top * 100 + '%',
              left: left * 100 + '%',
              fontSize: '26px',
              color: '#ff3b30',
              textShadow: '0 0 4px #fff, 0 0 4px #fff',
              marginLeft: '-13px',
              marginTop: '-13px'
            }}
          />
        )
      })}
    </div>
  )
}
const Gallery = ({ module, highlights }) => {
  return (
    <div className="icons-container">
      {(module.pictures || []).map((picture, index) => (
        <div style={{ flex: '200 0 0', textAlign: 'center', maxHeight: '200px' }} key={picture.file}>
          <BookResource
            id={picture.file}
            title={picture.caption}
            path={'pictures°|°' + index + '°|°file'}
            highlights={highlights}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      ))}
    </div>
  )
}
const ImageMcq = ({ module, highlights }) => {
  const { images, answers, noShuffle, doNotAskUserForConfirmation } = module
  const nbCorrect = (answers || []).length
  return (
    <div>
      <ModuleParam
        label="Réponses correctes"
        value={nbCorrect + ' (' + (nbCorrect > 1 ? 'choix multiple' : 'choix unique') + ')'}
      />
      {(noShuffle || doNotAskUserForConfirmation) && (
        <div style={{ marginBottom: '6px' }}>
          {noShuffle && <NoteFlag label="Ne pas mélanger les réponses" />}
          {doNotAskUserForConfirmation && <NoteFlag label="Ne pas demander la confirmation" />}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(' + (module.columns || 2) + ', 1fr)'
        }}
      >
        {(images || []).map((image, index) => {
          const valid = (answers || []).find(answer => answer === image._id)
          return (
            <div key={image._id} className={'image-mcq-cell' + (valid ? ' valid' : '')}>
              {valid && (
                <span className="mcq-correct-badge">
                  <i className="fa fa-check" /> bonne réponse
                </span>
              )}
              <BookResource
                id={image.picture}
                style={{ maxWidth: '100%', margin: '10px' }}
                path={'images°|°' + index}
                highlights={highlights}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const Level = ({ module, highlights }) => {
  const { codes, reward, objective, penalty, marble, levelBackground, levelCover } = module
  return (
    <div>
      <h4>Codes</h4>
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        {(codes || []).map((code, index) => (
          <QRCard key={index} code={code} />
        ))}
      </div>
      <h4>Score</h4>
      <div>
        <ModuleParam label="Récompense par QR code" value={reward} />
        <ModuleParam label="Pénalité (la bille touche le bord)" value={penalty} />
        <ModuleParam label="Objectif pour le score maximum" value={objective} />
      </div>
      <BookResource id={marble} path="marble" highlights={highlights} />
      <BookResource id={levelBackground} path="levelBackground" highlights={highlights} />
      <BookResource id={levelCover} path="levelCover" highlights={highlights} />
    </div>
  )
}

const Markdown = ({ module, highlights }) => {
  return <BookText id={module._id} collection="modules" path="code" text={module.code} highlights={highlights} />
}

const MCQ = ({ module, highlights }) => {
  const { propositions, answers, noShuffle, doNotAskUserForConfirmation } = module
  const nbCorrect = (answers || []).length
  return (
    <div>
      <ModuleParam
        label="Réponses correctes"
        value={nbCorrect + ' (' + (nbCorrect > 1 ? 'choix multiple' : 'choix unique') + ')'}
      />
      {(noShuffle || doNotAskUserForConfirmation) && (
        <div style={{ marginBottom: '6px' }}>
          {noShuffle && <NoteFlag label="Ne pas mélanger les réponses" />}
          {doNotAskUserForConfirmation && <NoteFlag label="Ne pas demander la confirmation" />}
        </div>
      )}
      <ul>
        {(propositions || []).map((proposition, index) => {
          const valid = (answers || []).find(answer => answer === proposition)
          return (
            <li
              key={index}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '2px 8px',
                marginBottom: '2px',
                borderRadius: '4px',
                background: valid ? '#e3efe0' : 'transparent',
                color: valid ? '#3d6b34' : 'inherit'
              }}
            >
              {valid && <i className="fa fa-check" />}
              <BookText
                collection="modules"
                id={module._id}
                path={'propositions°|°' + index}
                text={proposition}
                style={{ display: 'inline-block', marginTop: 0 }}
                highlights={highlights}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const RAD2DEG = 180 / Math.PI
function cartesianToPolar(coord) {
  var lon = Math.atan2(coord.x, -coord.z) * RAD2DEG
  var length = Math.sqrt(coord.x * coord.x + coord.z * coord.z)
  var lat = Math.atan2(coord.y, length) * RAD2DEG

  return { lon, lat }
}

const Photosphere = ({ module, highlights }) => {
  const { picture, orientation, differences } = module
  return (
    <div style={{ position: 'relative' }}>
      <BookResource id={picture} style={{ maxWidth: '100%' }} path="picture" highlights={highlights} />
      {(differences || []).map(difference => {
        const { lon, lat } = cartesianToPolar(difference)
        /* magic numbers everywhere, because fuck future me */
        const yaw = (lon + 270) % 360
        const pitch = lat
        const left = yaw / 360.0
        const top = 0.5 - pitch / 180
        return (
          <i
            key={difference._id}
            className="fa fa-crosshairs"
            style={{
              position: 'absolute',
              top: top * 100 + '%',
              left: left * 100 + '%',
              fontSize: '26px',
              color: '#ff3b30',
              textShadow: '0 0 4px #fff, 0 0 4px #fff',
              marginLeft: '-13px',
              marginTop: '-13px'
            }}
          />
        )
      })}
      orientation {orientation}° par rapport au nord
    </div>
  )
}

const Puzzle = ({ module, highlights }) => {
  const { picture, pieces, shuffleRotation } = module
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <h4>Fond du puzzle</h4>
          <BookResource
            id={picture}
            style={{ width: '100%' }}
            styleResource={{ width: '100%' }}
            path="picture"
            highlights={highlights}
          />
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <h4>Puzzle reconstitué</h4>
          <div style={{ position: 'relative' }}>
            <BookResource
              id={picture}
              style={{ width: '100%' }}
              styleResource={{ width: '100%' }}
              hd
              path="picture"
              highlights={highlights}
            />
            {(pieces || []).map(({ top, left, right, bottom, picture }, index) => {
              return (
                <BookResource
                  id={picture}
                  key={picture}
                  style={{
                    position: 'absolute',
                    top: top * 100 + '%',
                    left: left * 100 + '%',
                    width: (right - left) * 100 + '%',
                    height: (bottom - top) * 100 + '%'
                  }}
                  path={'pieces°|°' + index + '°|°picture'}
                  highlights={highlights}
                  styleResource={{ width: '100%', maxWidth: 'auto' }}
                />
              )
            })}
          </div>
        </div>
      </div>
      <BoolFlag label="Faire tourner les pièces" value={shuffleRotation} />
    </div>
  )
}
const CodeChip = ({ value }) => (
  <span
    style={{
      fontFamily: 'monospace',
      fontSize: '0.8em',
      fontWeight: 'bold',
      background: '#f5f5f5',
      borderRadius: '3px',
      padding: '2px 6px',
      marginTop: '6px',
      wordBreak: 'break-all',
      display: 'inline-block'
    }}
  >
    {'CODE : ' + value}
  </span>
)

const MissingValue = ({ children }) => <em style={{ color: '#bbb' }}>{children}</em>

const ValidationCard = ({ title, note, actions, chapters, children }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: '#fff',
      border: '1px solid #e5e5e5',
      borderRadius: '6px',
      padding: '10px',
      margin: '6px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
      textAlign: 'center',
      minWidth: '120px'
    }}
  >
    {title && (
      <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '6px', color: '#333', wordBreak: 'break-word' }}>
        {title}
      </div>
    )}
    {children}
    {note && <div style={{ fontSize: '0.75em', color: '#999', marginTop: '4px' }}>{note}</div>}
    {actions && actions.length > 0 && (
      <div style={{ marginTop: '6px', fontSize: '0.85em' }}>
        {actions.map((action, i) => (
          <BookAction action={action} chapters={chapters} key={i} />
        ))}
      </div>
    )}
  </div>
)

const QRCodeHunt = ({ module }) => {
  const codes = module.codes || []
  return (
    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
      {(codes || []).map((code, index) => (
        <QRCard key={index} code={code} />
      ))}
    </div>
  )
}

const Quizz = ({ module, highlights }) => {
  const answers = module.answers
  if (!answers || answers.length === 0) {
    return <div>pas de bonne réponse</div>
  }
  if (answers.length === 1) {
    return <div>La bonne réponse est {answers[0]}</div>
  }
  return (
    <div>
      Les bonnes réponses sont
      <ul>
        {(module.answers || []).map((answer, index) => (
          <li key={index}>
            <BookText text={answer} collection="modules" id={module._id} path={'answers°|°' + index} highlights={highlights} />
          </li>
        ))}
      </ul>
    </div>
  )
}

const Rating = ({ module, highlights }) => {
  const ratings = module.ratings || []
  if (ratings.length === 0) {
    return <div>pas de critère de note défini</div>
  }
  return (
    <ul>
      {(ratings || []).map(({ key, label }, index) => (
        <li key={key}>
          <BookText text={label} collection="modules" id={module._id} path={'ratings°|°' + index} highlights={highlights} />
        </li>
      ))}
    </ul>
  )
}

const RatingStore = ({ module, highlights }) => {
  const ratings = module.ratings || []
  if (ratings.length === 0) {
    return <div>pas de critère de note défini</div>
  }
  return (
    <ul>
      {(ratings || []).map(({ key, label }, index) => (
        <li key={key}>
          <BookText text={label} collection="modules" id={module._id} path={'ratings°|°' + index} highlights={highlights} />
        </li>
      ))}
    </ul>
  )
}

const Scratch = ({ module, highlights }) => {
  const foreground = module.foreground

  return <BookResource id={foreground} title="image de surcouche" path="foreground" highlights={highlights} />
}
const Selfie = ({ module, highlights }) => {
  const [landscape, portrait] = module.tags || []
  return (
    <div>
      <BookText text={module.shareText} collection="modules" id={module._id} path="shareText" highlights={highlights} />
      surcouche en mode paysage
      <BookResource
        id={landscape && landscape.file}
        style={{ display: 'inline-block' }}
        path="tags°|°0°|°file"
        highlights={highlights}
      />
      <br />
      surcouche en mode portrait
      <BookResource
        id={portrait && portrait.file}
        style={{ display: 'inline-block' }}
        path="tags°|°1°|°file"
        highlights={highlights}
      />
      <br />
      <BoolFlag label="Partage sur les réseaux sociaux" value={module.allowSharing} />
    </div>
  )
}

const Session = ({ module }) => {
  const { askForName, askForEmail } = module
  return (
    <div>
      <BoolFlag label="Demander le nom d'équipe" value={askForName} />
      <BoolFlag label="Demander l'adresse email" value={askForEmail} />
    </div>
  )
}

const ShakeContext = ({ module }) => {
  const { objective } = module
  return <ModuleParam label="Objectif pour le score maximum" value={objective} />
}

const Slasher = ({ module, highlights }) => {
  const { entities, objective } = module
  return (
    <div>
      <ModuleParam label="Objectif pour le score maximum" value={objective} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {(entities || []).map(({ slashedFrames, normalFrames, sounds, points, rotation, probability }, index) => {
          return (
            <div className={'slasher-entity' + (checkPath(module, 'entities°|°' + index) ? ' highlighted' : '')} key={index}>
              <div className="field-label">Normal</div>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2px' }}>
                {(normalFrames || []).map?.((f, i) => (
                  <BookResource id={f} key={i} style={{ maxWidth: '80px' }} />
                ))}
              </div>
              <div className="field-label">Touché</div>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2px' }}>
                {(slashedFrames || []).map?.((f, i) => (
                  <BookResource id={f} key={i} style={{ maxWidth: '80px' }} />
                ))}
              </div>
              {(sounds || []).map?.((f, i) => (
                <BookResource id={f} key={i} />
              ))}
              <div className="slasher-entity-meta">
                <span>
                  <i className="fa fa-trophy" /> {points} pts
                </span>
                <span>proba {probability}</span>
              </div>
              <BoolFlag label="Rotation" value={rotation} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const Video = ({ module, highlights }) => {
  const { video, text, autoplay } = module
  return (
    <div>
      <BookResource id={video} path="video" highlights={highlights} />
      <BookText text={text} collection="modules" id={module._id} path="text" highlights={highlights} />
      <BoolFlag label="Lecture automatique" value={autoplay} />
    </div>
  )
}

const Score = ({ module }) => {
  const { scoreType, showDetails } = module
  const scoreTypeLabel = {
    scenario: 'Points gagnés pendant ce scénario',
    track: 'Points gagnés sur ce parcours',
    experience: 'Points gagnés sur cette expérience'
  }
  return (
    <ul>
      <li>Type de score : {scoreTypeLabel[scoreType] || scoreType || 'non défini'}</li>
      <li>Détail du score : {showDetails ? 'affiché' : 'masqué'}</li>
    </ul>
  )
}

const Pairing = ({ module, highlights }) => {
  return null
}

const Object3d = ({ module }) => {
  const { object } = module
  const { model } = object || {}
  return (
    <ul>
      <li>Modèle 3D : {model ? 'fourni (archive .zip)' : 'non fourni'}</li>
    </ul>
  )
}

const Sonometer = ({ module, highlights }) => {
  const { counterBackground, counterNeedle, counterForeground, minLevel, level, maxLevel } = module
  return (
    <div>
      <ul>
        <li>Niveau minimum du son : {minLevel != null ? minLevel : 'non défini'}</li>
        <li>Niveau pour marquer des points : {level != null ? level : 'non défini'}</li>
        <li>Niveau maximum du son : {maxLevel != null ? maxLevel : 'non défini'}</li>
      </ul>
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        <BookResource
          id={counterBackground}
          title="Fond"
          path="counterBackground"
          highlights={highlights}
          style={{ maxWidth: '120px', margin: '4px' }}
        />
        <BookResource
          id={counterNeedle}
          title="Aiguille"
          path="counterNeedle"
          highlights={highlights}
          style={{ maxWidth: '120px', margin: '4px' }}
        />
        <BookResource
          id={counterForeground}
          title="Surcouche"
          path="counterForeground"
          highlights={highlights}
          style={{ maxWidth: '120px', margin: '4px' }}
        />
      </div>
    </div>
  )
}

const Draw = ({ module, highlights }) => {
  const { image, toolBar } = module
  return (
    <div>
      {image && <BookResource id={image} title="Image superposée" path="image" highlights={highlights} />}

      {toolBar && (toolBar.position || toolBar.orientation) && (
        <ul>
          {toolBar.position && <li>Position de la barre d'outils : {toolBar.position}</li>}
          {toolBar.orientation && <li>Orientation : {toolBar.orientation}</li>}
        </ul>
      )}
    </div>
  )
}

const CONDITION_SYMBOLS = {
  equal: '=',
  contain: '∈',
  exist: '∃',
  greater: '>',
  less: '<',
  isSolution: 'est solution',
  isNotSolution: "n'est pas solution"
}

const Conditions = ({ conditions }) => {
  if (!conditions || conditions.length === 0) return null
  return (
    <ul>
      {conditions.map((c, i) => (
        <li key={i}>
          <code>{c.key}</code> {CONDITION_SYMBOLS[c.method] || c.method}
          {c.value != null && c.value !== '' ? (
            <>
              {' '}
              <code>{String(c.value)}</code>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

const Toolbox = ({ module, chapters, highlights }) => {
  const { picture, drops, listToolBoxButtons } = module
  const dropIndexes = {}
  ;(drops || []).forEach(({ _id }, index) => (dropIndexes[_id] = index + 1))

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <BookResource
          id={picture}
          style={{ width: '100%', maxWidth: 'auto' }}
          styleResource={{ width: '100%', maxWidth: 'auto' }}
          path="picture"
          highlights={highlights}
        />
        {(drops || []).map(({ _id, left, right, bottom, top }, index) => (
          <div
            key={_id || index}
            style={{
              position: 'absolute',
              top: top * 100 + '%',
              left: left * 100 + '%',
              width: (right - left) * 100 + '%',
              height: (bottom - top) * 100 + '%',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px dashed white',
              boxShadow: '1px 1px 12px #555',
              color: 'white',
              textShadow: `-1px 0 0 rgba(0, 0, 0, 0.8),
             1px 0 0 rgba(0, 0, 0, 0.8),
             0 -1px 0 rgba(0, 0, 0, 0.8),
             0 1px 0 rgba(0, 0, 0, 0.8)`,
              textAlign: 'center',
              zIndex: 1,
              overflow: 'hidden',
              borderRadius: '6px',
              pointerEvents: 'none'
            }}
          >
            {index + 1}
          </div>
        ))}
      </div>
      <h3>Boutons</h3>
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        {(listToolBoxButtons || []).map((button, bIndex) => (
          <ValidationCard
            key={bIndex}
            title={`Bouton ${bIndex + 1}${button.zone && dropIndexes[button.zone] ? ' — Zone ' + dropIndexes[button.zone] : ''}`}
          >
            {(button.states || []).map((state, sIndex) => (
              <div key={sIndex} style={{ marginTop: sIndex > 0 ? '8px' : 0 }}>
                <BookResource
                  id={state.icon}
                  path={'listToolBoxButtons°|°' + bIndex + '°|°states°|°' + sIndex + '°|°icon'}
                  highlights={highlights}
                  style={{ maxWidth: '64px', margin: '4px' }}
                />
                <Conditions conditions={state.conditions} />
                {(state.actions || []).map((action, i) => (
                  <BookAction action={action} chapters={chapters} key={i} />
                ))}
              </div>
            ))}
          </ValidationCard>
        ))}
      </div>
    </div>
  )
}

const Cluedo = ({ module, chapters, highlights }) => {
  const { mode, cluedo_id, points, clue_text, clues, max_clues, success, failure } = module
  const modeLabel = { hypothesis: 'Hypothèse', choice: 'Choix', accusation: 'Désignation' }
  return (
    <div>
      <ul>
        <li>Mode de jeu : {modeLabel[mode] || mode || 'non défini'}</li>
        {cluedo_id && <li>Enquête : {cluedo_id}</li>}
        {points != null && <li>Points par bonne réponse : {points}</li>}
        {max_clues != null && <li>Nombre d'indices maximum : {max_clues}</li>}
      </ul>
      {clue_text && <BookText text={clue_text} collection="modules" id={module._id} path="clue_text" highlights={highlights} />}
      {(clues || []).length > 0 && <h4>Indices</h4>}
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        {(clues || []).map((clue, index) => (
          <ValidationCard key={clue._id || index} title={clue.title} actions={clue.actions} chapters={chapters}>
            <BookResource
              id={clue.image}
              path={'clues°|°' + index + '°|°image'}
              highlights={highlights}
              style={{ maxWidth: '150px' }}
            />
            <BookText
              text={clue.text}
              collection="modules"
              id={module._id}
              path={'clues°|°' + index + '°|°text'}
              highlights={highlights}
            />
          </ValidationCard>
        ))}
      </div>
      {(success || []).length > 0 && (
        <div>
          <h4>Actions en cas de succès</h4>
          {success.map((action, i) => (
            <BookAction action={action} chapters={chapters} key={i} />
          ))}
        </div>
      )}
      {(failure || []).length > 0 && (
        <div>
          <h4>Actions en cas d'échec</h4>
          {failure.map((action, i) => (
            <BookAction action={action} chapters={chapters} key={i} />
          ))}
        </div>
      )}
    </div>
  )
}

const Silence = ({ module, highlights }) => {
  const {
    startScore,
    reward,
    objective,
    penalty,
    minLevel,
    level,
    maxLevel,
    codes,
    orderedScan,
    infiniteScan,
    maxScanNumber,
    counterBackground,
    counterNeedle,
    counterCover
  } = module
  return (
    <div>
      <h4>Score</h4>
      <ul>
        <li>Score de départ : {startScore != null ? startScore : '—'}</li>
        <li>Récompense par QR code : {reward != null ? reward : '—'}</li>
        <li>Objectif : {objective != null ? objective : '—'}</li>
        <li>Pénalité si le son dépasse la limite : {penalty != null ? penalty : '—'}</li>
      </ul>
      <h4>Niveaux sonores</h4>
      <ul>
        <li>Minimum : {minLevel != null ? minLevel : '—'}</li>
        <li>Limite (perte de points au-delà) : {level != null ? level : '—'}</li>
        <li>Maximum : {maxLevel != null ? maxLevel : '—'}</li>
      </ul>
      {(orderedScan || infiniteScan || maxScanNumber != null) && (
        <ul>
          {orderedScan && <li>Ordre de validation forcé</li>}
          {infiniteScan && <li>Validation illimitée</li>}
          {maxScanNumber != null && <li>Nombre maximum de QR codes : {maxScanNumber}</li>}
        </ul>
      )}
      {(codes || []).length > 0 && <h4>Codes valides</h4>}
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        {(codes || []).map((c, index) => (
          <QRCard key={index} code={c} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        <BookResource
          id={counterBackground}
          title="Fond"
          path="counterBackground"
          highlights={highlights}
          style={{ maxWidth: '120px', margin: '4px' }}
        />
        <BookResource
          id={counterNeedle}
          title="Aiguille"
          path="counterNeedle"
          highlights={highlights}
          style={{ maxWidth: '120px', margin: '4px' }}
        />
        <BookResource
          id={counterCover}
          title="Surcouche"
          path="counterCover"
          highlights={highlights}
          style={{ maxWidth: '120px', margin: '4px' }}
        />
      </div>
    </div>
  )
}

const Validation = ({ module, chapters, highlights }) => {
  const { mode, overlayImage, elements } = module
  const modeLabel = {
    qrcode: 'Validation par QR code',
    code: 'Validation par mot code',
    image: 'Validation par image'
  }
  return (
    <div>
      <p>Méthode : {modeLabel[mode] || mode || 'non défini'}</p>
      {overlayImage && <BookResource id={overlayImage} title="Surcouche" path="overlayImage" highlights={highlights} />}
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        {(elements || []).map((el, index) => (
          <ValidationCard
            key={el._id || index}
            title={el.internalName}
            note={el.doNotRevalidate ? 'Validation unique' : null}
            actions={el.actions}
            chapters={chapters}
          >
            {mode === 'code' &&
              (el.validationCode ? <CodeChip value={el.validationCode} /> : <MissingValue>code manquant</MissingValue>)}
            {mode === 'qrcode' &&
              (el.validationQRCode ? (
                <>
                  <QRCodeReact value={el.validationQRCode} size={120} />
                  <CodeChip value={el.validationQRCode} />
                </>
              ) : (
                <MissingValue>QR code manquant</MissingValue>
              ))}
            {mode === 'image' &&
              (el.validationImage ? (
                <BookResource
                  id={el.validationImage}
                  path={'elements°|°' + index + '°|°validationImage'}
                  highlights={highlights}
                  style={{ maxWidth: '140px' }}
                />
              ) : (
                <MissingValue>image manquante</MissingValue>
              ))}
          </ValidationCard>
        ))}
      </div>
    </div>
  )
}

const SwitchModule = ({ module, chapters }) => {
  return null
}

export default class extends Component {
  state = {}
  async componentDidMount() {
    const check = !this.props.nonDiagnostic && (await checks.checkModule(this.props.module))
    this.setState({ check })
  }

  render() {
    const { check } = this.state
    const { module, _id, url, chapters, score, nonDiagnostic, highlights } = this.props
    const {
      type,
      instruction,
      longInstruction,
      internalName,
      name,
      title,
      toolButton,
      listToolButtons,
      timeLimit,
      canGoBack,
      autoNext,
      hideNextButton,
      doNotDisplayScore,
      timerMalus,
      retries
    } = module

    let specific
    switch (type) {
      case 'augmented_reality':
        specific = <AugmentedReality {...this.props} />
        break
      case 'differences':
        specific = <Differences {...this.props} />
        break
      case 'gallery':
        specific = <Gallery {...this.props} />
        break
      case 'score_conversation':
      case 'conversation':
        specific = <Conversation {...this.props} />
        break
      case 'compass':
        specific = <Compass {...this.props} />
        break

      case 'diaporama':
      case 'score_diaporama':
      case 'picture_slider':
        specific = <Diaporama {...this.props} />
        break
      case 'dragdrop':
        specific = <DragDrop {...this.props} />
        break
      case 'image_mcq':
        specific = <ImageMcq {...this.props} />
        break
      case 'level':
        specific = <Level {...this.props} />
        break
      case 'markdown':
        specific = <Markdown {...this.props} />
        break
      case 'mcq':
        specific = <MCQ {...this.props} />
        break
      case 'photosphere':
      case 'differences_photosphere':
        specific = <Photosphere {...this.props} />
        break
      case 'puzzle':
        specific = <Puzzle {...this.props} />
        break
      case 'quizz':
        specific = <Quizz {...this.props} />
        break
      case 'qrcode_hunt':
        specific = <QRCodeHunt {...this.props} />
        break
      case 'rating':
        specific = <Rating {...this.props} />
        break
      case 'rating_store':
        specific = <RatingStore {...this.props} />
        break
      case 'choice':
        specific = <Choice {...this.props} />
        break

      case 'scratch':
        specific = <Scratch {...this.props} />
        break
      case 'selfie':
        specific = <Selfie {...this.props} />
        break
      case 'session':
        specific = <Session {...this.props} />
        break
      case 'shake_contest':
        specific = <ShakeContext {...this.props} />
        break
      case 'slasher':
        specific = <Slasher {...this.props} />
        break
      case 'video':
        specific = <Video {...this.props} />
        break
      case 'score':
        specific = <Score {...this.props} />
        break
      case 'pairing':
        specific = <Pairing {...this.props} />
        break
      case 'object3d':
        specific = <Object3d {...this.props} />
        break
      case 'sonometer':
        specific = <Sonometer {...this.props} />
        break
      case 'draw':
        specific = <Draw {...this.props} />
        break
      case 'toolbox':
        specific = <Toolbox {...this.props} />
        break
      case 'cluedo':
        specific = <Cluedo {...this.props} />
        break
      case 'silence':
        specific = <Silence {...this.props} />
        break
      case 'validation':
        specific = <Validation {...this.props} />
        break
      case 'switch':
        specific = <SwitchModule {...this.props} />
        break
      default:
        specific = <pre>{/* JSON.stringify(module, null, 2) */}Non géré</pre>
    }

    return (
      <article className={'module ' + type} id={_id}>
        <h3>
          <BookText
            text={name}
            collection="modules"
            id={_id}
            path="name"
            highlights={highlights}
            style={{ display: 'inline-block' }}
          />
          {!name && (
            <BookText
              text={title}
              collection="modules"
              id={_id}
              path="title"
              highlights={highlights}
              style={{ display: 'inline-block' }}
            />
          )}
          {internalName && (
            <BookText
              text={internalName}
              collection="modules"
              id={_id}
              path="internalName"
              highlights={highlights}
              className="internal"
              style={{ display: 'inline-block' }}
            />
          )}
          <Link to={url}>
            <i className="fa fa-edit" />
          </Link>
        </h3>
        <section>
          <aside className="common">
            <span className="module-type-badge">Module : {moduleLabel[module.type] || module.type}</span>
            <br />
            {instruction && (
              <BookText text={instruction} collection="modules" id={_id} path="instruction" highlights={highlights} />
            )}
            {longInstruction && (
              <BookText text={longInstruction} collection="modules" id={_id} path="longInstruction" highlights={highlights} />
            )}
            {
              <BookResource
                id={module.background}
                style={{ maxWidth: '100%', display: 'inline-block' }}
                path="background"
                highlights={highlights}
              />
            }
            {<BookResource id={module.music} path="music" highlights={highlights} />}
            {module.longInstructionImage && (
              <BookResource
                id={module.longInstructionImage}
                path="longInstructionImage"
                highlights={highlights}
                style={{ maxWidth: '100%' }}
              />
            )}
            {(timeLimit > 0 || score > 0) && (
              <div className="aside-metrics">
                {timeLimit > 0 && (
                  <span className="aside-metric">
                    <i className="fa fa-clock-o" /> {timeLimit} s
                  </span>
                )}
                {score > 0 && (
                  <span className="aside-metric">
                    <i className="fa fa-trophy" /> {score} pts
                  </span>
                )}
              </div>
            )}
            <div>
              <BoolFlag label="Retour en arrière" value={canGoBack} />
              {autoNext && <BoolFlag label="Passage auto à la suite" value />}
              {hideNextButton && <BoolFlag label="Bouton suivant masqué" value={false} />}
              {doNotDisplayScore && <BoolFlag label="Score de fin masqué" value={false} />}
            </div>
            <ModuleParam label="Malus de rapidité (score max perdu)" value={timerMalus > 0 ? timerMalus : null} />
            {toolButton && toolButton.action && (
              <div className="aside-media-group">
                <div className="field-label">Bouton d'action</div>
                <div className="tool-buttons">
                  <ToolButtonCard
                    icon={toolButton.icon}
                    action={toolButton.action}
                    path="toolButton°|°icon"
                    chapters={chapters}
                    highlights={highlights}
                  />
                </div>
              </div>
            )}
            {listToolButtons && listToolButtons.length > 0 && (
              <div className="aside-media-group">
                <div className="field-label">Boutons d'action</div>
                <div className="tool-buttons">
                  {listToolButtons.map(
                    (b, i) =>
                      b &&
                      b.action && (
                        <ToolButtonCard
                          key={i}
                          icon={b.icon}
                          action={b.action}
                          path={'listToolButtons°|°' + i + '°|°icon'}
                          chapters={chapters}
                          highlights={highlights}
                        />
                      )
                  )}
                </div>
              </div>
            )}
            <Retries retries={retries} />
          </aside>
          <div>{specific}</div>
          {!nonDiagnostic && <Diagnostic checks={check} />}
        </section>
      </article>
    )
  }
}
